<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  selectedBoardId: { type: [Number, String], default: null },
});

const emit = defineEmits(['updated', 'boardSelected']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const boards = useMapGetter('kanban/getBoards');
const stages = useMapGetter('kanban/getStages');

const activeBoardId = ref(props.selectedBoardId);
const localStages = ref([]);

// Board form
const boardName = ref('');
const boardDescription = ref('');
const editingBoardId = ref(null);

// Stage form
const stageName = ref('');
const stageColor = ref('#1f93ff');
const editingStageId = ref(null);

const activeBoard = computed(() =>
  boards.value.find(b => b.id === Number(activeBoardId.value))
);

watch(stages, newStages => {
  localStages.value = [...newStages];
});

const resetBoardForm = () => {
  boardName.value = '';
  boardDescription.value = '';
  editingBoardId.value = null;
};

const resetStageForm = () => {
  stageName.value = '';
  stageColor.value = '#1f93ff';
  editingStageId.value = null;
};

const loadStages = boardId => {
  if (boardId) store.dispatch('kanban/getStages', boardId);
};

const selectBoard = boardId => {
  activeBoardId.value = boardId;
  resetStageForm();
  loadStages(boardId);
  emit('boardSelected', boardId);
};

const saveBoard = async () => {
  if (!boardName.value) return;
  try {
    if (editingBoardId.value) {
      await store.dispatch('kanban/updateBoard', {
        id: editingBoardId.value,
        name: boardName.value,
        description: boardDescription.value,
      });
    } else {
      const board = await store.dispatch('kanban/createBoard', {
        name: boardName.value,
        description: boardDescription.value,
        position: boards.value.length,
      });
      selectBoard(board.id);
    }
    useAlert(t('KANBAN.API.BOARD_SAVE_SUCCESS'));
    resetBoardForm();
    emit('updated');
  } catch (error) {
    useAlert(t('KANBAN.API.BOARD_SAVE_ERROR'));
  }
};

const editBoard = board => {
  editingBoardId.value = board.id;
  boardName.value = board.name;
  boardDescription.value = board.description || '';
};

const deleteBoard = async board => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.MANAGE_MODAL.DELETE_BOARD_CONFIRM'))) return;
  try {
    await store.dispatch('kanban/deleteBoard', board.id);
    useAlert(t('KANBAN.API.BOARD_DELETE_SUCCESS'));
    if (activeBoardId.value === board.id) {
      activeBoardId.value = boards.value[0]?.id || null;
      emit('boardSelected', activeBoardId.value);
    }
    emit('updated');
  } catch (error) {
    useAlert(t('KANBAN.API.BOARD_DELETE_ERROR'));
  }
};

const saveStage = async () => {
  if (!stageName.value || !activeBoardId.value) return;
  try {
    if (editingStageId.value) {
      await store.dispatch('kanban/updateStage', {
        boardId: activeBoardId.value,
        id: editingStageId.value,
        name: stageName.value,
        color: stageColor.value,
      });
    } else {
      await store.dispatch('kanban/createStage', {
        boardId: activeBoardId.value,
        name: stageName.value,
        color: stageColor.value,
        position: localStages.value.length,
      });
    }
    useAlert(t('KANBAN.API.STAGE_SAVE_SUCCESS'));
    resetStageForm();
    emit('updated');
  } catch (error) {
    useAlert(t('KANBAN.API.STAGE_SAVE_ERROR'));
  }
};

const editStage = stage => {
  editingStageId.value = stage.id;
  stageName.value = stage.name;
  stageColor.value = stage.color || '#1f93ff';
};

const deleteStage = async stage => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.MANAGE_MODAL.DELETE_STAGE_CONFIRM'))) return;
  try {
    await store.dispatch('kanban/deleteStage', {
      boardId: activeBoardId.value,
      id: stage.id,
    });
    useAlert(t('KANBAN.API.STAGE_DELETE_SUCCESS'));
    emit('updated');
  } catch (error) {
    useAlert(t('KANBAN.API.STAGE_DELETE_ERROR'));
  }
};

const onReorderStages = async () => {
  try {
    await store.dispatch('kanban/reorderStages', {
      boardId: activeBoardId.value,
      stageIds: localStages.value.map(s => s.id),
    });
    emit('updated');
  } catch (error) {
    useAlert(t('KANBAN.API.REORDER_ERROR'));
    loadStages(activeBoardId.value);
  }
};

const open = () => {
  activeBoardId.value = props.selectedBoardId || boards.value[0]?.id || null;
  resetBoardForm();
  resetStageForm();
  if (activeBoardId.value) loadStages(activeBoardId.value);
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="t('KANBAN.MANAGE_MODAL.TITLE')"
    :show-confirm-button="false"
    :cancel-button-label="t('KANBAN.MANAGE_MODAL.CLOSE')"
  >
    <div class="grid grid-cols-2 gap-6">
      <!-- Boards column -->
      <div class="flex flex-col gap-3">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('KANBAN.MANAGE_MODAL.BOARDS_TITLE') }}
        </h4>
        <div class="flex flex-col gap-1 overflow-auto max-h-40">
          <div
            v-for="board in boards"
            :key="board.id"
            class="flex items-center justify-between gap-2 p-2 rounded-lg group"
            :class="
              activeBoardId === board.id ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'
            "
          >
            <button
              type="button"
              class="flex-1 text-sm text-left truncate text-n-slate-12"
              @click="selectBoard(board.id)"
            >
              {{ board.name }}
            </button>
            <div class="items-center hidden gap-1 group-hover:flex">
              <button
                type="button"
                class="p-1 rounded text-n-slate-10 hover:text-n-slate-12"
                @click="editBoard(board)"
              >
                <Icon icon="i-lucide-pencil" class="size-3.5" />
              </button>
              <button
                type="button"
                class="p-1 rounded text-n-slate-10 hover:text-n-ruby-9"
                @click="deleteBoard(board)"
              >
                <Icon icon="i-lucide-trash-2" class="size-3.5" />
              </button>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2 pt-2 border-t border-n-weak">
          <Input
            v-model="boardName"
            :placeholder="t('KANBAN.MANAGE_MODAL.BOARD_NAME')"
            size="sm"
          />
          <Input
            v-model="boardDescription"
            :placeholder="t('KANBAN.MANAGE_MODAL.BOARD_DESCRIPTION')"
            size="sm"
          />
          <NextButton
            sm
            blue
            :label="
              editingBoardId
                ? t('KANBAN.MANAGE_MODAL.SAVE')
                : t('KANBAN.MANAGE_MODAL.NEW_BOARD')
            "
            @click="saveBoard"
          />
        </div>
      </div>

      <!-- Stages column -->
      <div class="flex flex-col gap-3">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('KANBAN.MANAGE_MODAL.STAGES_TITLE') }}
        </h4>
        <p
          v-if="!activeBoardId"
          class="text-sm text-n-slate-11"
        >
          {{ t('KANBAN.MANAGE_MODAL.SELECT_BOARD_FIRST') }}
        </p>
        <template v-else>
          <Draggable
            :list="localStages"
            item-key="id"
            handle=".stage-drag-handle"
            animation="150"
            class="flex flex-col gap-1 overflow-auto max-h-40"
            @end="onReorderStages"
          >
            <template #item="{ element }">
              <div
                class="flex items-center justify-between gap-2 p-2 rounded-lg group hover:bg-n-alpha-1"
              >
                <span
                  class="cursor-grab stage-drag-handle text-n-slate-10"
                >
                  <Icon icon="i-lucide-grip-vertical" class="size-3.5" />
                </span>
                <span
                  class="size-3 rounded-full"
                  :style="{ backgroundColor: element.color || '#1f93ff' }"
                />
                <span class="flex-1 text-sm truncate text-n-slate-12">
                  {{ element.name }}
                </span>
                <div class="items-center hidden gap-1 group-hover:flex">
                  <button
                    type="button"
                    class="p-1 rounded text-n-slate-10 hover:text-n-slate-12"
                    @click="editStage(element)"
                  >
                    <Icon icon="i-lucide-pencil" class="size-3.5" />
                  </button>
                  <button
                    type="button"
                    class="p-1 rounded text-n-slate-10 hover:text-n-ruby-9"
                    @click="deleteStage(element)"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-3.5" />
                  </button>
                </div>
              </div>
            </template>
          </Draggable>
          <div class="flex flex-col gap-2 pt-2 border-t border-n-weak">
            <Input
              v-model="stageName"
              :placeholder="t('KANBAN.MANAGE_MODAL.STAGE_NAME')"
              size="sm"
            />
            <div class="flex items-center gap-2">
              <input
                v-model="stageColor"
                type="color"
                class="w-10 h-8 border rounded cursor-pointer border-n-weak"
              />
              <NextButton
                sm
                blue
                class="flex-1"
                :label="
                  editingStageId
                    ? t('KANBAN.MANAGE_MODAL.SAVE')
                    : t('KANBAN.MANAGE_MODAL.NEW_STAGE')
                "
                @click="saveStage"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </Dialog>
</template>
